import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { AlphaApiError, createAlphaBatch } from "../api/alphaRuns.api";
import { alphaRunsKey } from "./useAlphaRunsQuery";

const BATCH_ERROR_MESSAGES: Record<string, string> = {
  BATCH_NOT_DELIVERED: "Your first ads aren't ready yet.",
  NO_EXTRA_BATCH: "You've already generated your new ads.",
  RUN_ACTIVE: "Your ads are still being generated.",
};

const GENERIC_BATCH_ERROR = "Couldn't generate new ads. Please try again.";

export function useCreateAlphaBatch({
  creationId,
  onCreated,
  requestId,
}: {
  creationId: string;
  onCreated: () => void;
  requestId: number;
}): {
  create: (prompt: string) => void;
  errorMessage: null | string;
  isCreating: boolean;
  reset: () => void;
} {
  const queryClient = useQueryClient();
  const idempotencyKey = useRef<null | string>(null);

  const mutation = useMutation({
    mutationFn: (prompt: string) => {
      idempotencyKey.current ??= crypto.randomUUID();

      return createAlphaBatch({
        creationId,
        idempotencyKey: idempotencyKey.current,
        prompt,
        requestId,
      });
    },
    onError: (error) => {
      // Only transient failures retry the same attempt; a 4xx is a final answer.
      if (error instanceof AlphaApiError && error.status < 500) {
        idempotencyKey.current = null;
      }
    },
    onSuccess: () => {
      idempotencyKey.current = null;
      void queryClient.invalidateQueries({ queryKey: alphaRunsKey(requestId) });
      onCreated();
    },
  });

  const { error } = mutation;

  return {
    create: mutation.mutate,
    errorMessage: error
      ? (error instanceof AlphaApiError &&
          error.code &&
          BATCH_ERROR_MESSAGES[error.code]) ||
        GENERIC_BATCH_ERROR
      : null,
    isCreating: mutation.isPending,
    reset: mutation.reset,
  };
}
